/**
 * Global Service Request Page - KEIKKA Platform
 * Create and browse service requests with cross-border support
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, useLanguage } from '@/lib/context';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { useTranslatedContent } from '@/hooks/useContentTranslation';
import BudgetSection from '@/components/BudgetSection';
import { 
  Search, MapPin, Globe, Truck, Zap, Clock, Building2,
  ChevronRight, Filter, Plus, Loader2, CheckCircle2,
  AlertCircle, User, Phone, Mail, Send, ArrowRight, Languages,
  Package, DollarSign, Calendar, Users, Star, Sparkles,
  X, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import { LocationAutocomplete } from '@/components/LocationAutocomplete';
import { getCountryByCode, ALL_COUNTRIES } from '@/lib/countries-data';

const API = process.env.REACT_APP_BACKEND_URL;

// Category configuration - now using i18n keys
const CATEGORY_GROUPS = {
  trades: { nameKey: "categories.trades", icon: Building2, color: "bg-orange-100 text-orange-700" },
  transport: { nameKey: "categories.transport", icon: Truck, color: "bg-blue-100 text-blue-700" },
  auto: { nameKey: "categories.auto", icon: Zap, color: "bg-red-100 text-red-700" },
  maintenance: { nameKey: "categories.maintenance", icon: Sparkles, color: "bg-green-100 text-green-700" },
  tech: { nameKey: "categories.tech", icon: Globe, color: "bg-purple-100 text-purple-700" },
  professional: { nameKey: "categories.professional", icon: Users, color: "bg-indigo-100 text-indigo-700" },
  manufacturing: { nameKey: "categories.manufacturing", icon: Package, color: "bg-gray-100 text-gray-700" },
};

const URGENCY_OPTIONS = [
  { value: "low", labelKey: "urgency.low", descriptionKey: "urgency.lowDesc", color: "bg-gray-100 text-gray-700" },
  { value: "normal", labelKey: "urgency.normal", descriptionKey: "urgency.normalDesc", color: "bg-blue-100 text-blue-700" },
  { value: "high", labelKey: "urgency.high", descriptionKey: "urgency.highDesc", color: "bg-orange-100 text-orange-700" },
  { value: "urgent", labelKey: "urgency.urgent", descriptionKey: "urgency.urgentDesc", color: "bg-red-100 text-red-700" },
];

// Duration options (MANDATORY field)
const DURATION_OPTIONS = [
  { value: "1_day", labelKey: "duration.1day", descriptionKey: "duration.1dayDesc" },
  { value: "2_3_days", labelKey: "duration.2_3days", descriptionKey: "duration.2_3daysDesc" },
  { value: "1_week", labelKey: "duration.1week", descriptionKey: "duration.1weekDesc" },
  { value: "2_4_weeks", labelKey: "duration.2_4weeks", descriptionKey: "duration.2_4weeksDesc" },
  { value: "1_3_months", labelKey: "duration.1_3months", descriptionKey: "duration.1_3monthsDesc" },
  { value: "3_6_months", labelKey: "duration.3_6months", descriptionKey: "duration.3_6monthsDesc" },
  { value: "ongoing", labelKey: "duration.ongoing", descriptionKey: "duration.ongoingDesc" },
];

// Service Request Card Component
function ServiceRequestCard({ request, onClick }) {
  const { t } = useLanguage();
  const { text: translatedTitle, isTranslated: titleTranslated } = useTranslatedContent(request.title);
  const { text: translatedDesc, isTranslated: descTranslated } = useTranslatedContent(request.description);
  const isContentTranslated = titleTranslated || descTranslated;
  
  const urgencyConfig = URGENCY_OPTIONS.find(u => u.value === request.urgency) || URGENCY_OPTIONS[1];
  const durationConfig = DURATION_OPTIONS.find(d => d.value === request.duration_needed);
  
  // Handle country - could be ISO code (FI) or full name (Finland)
  // NO defaultValue - let i18n system handle translation properly
  const getLocalizedCountryName = (countryValue) => {
    if (!countryValue) return '';
    
    // First try as ISO code
    const countryByCode = getCountryByCode(countryValue);
    if (countryByCode) {
      const key = `location.countries.${countryValue}`;
      const translated = t(key);
      return translated !== key ? translated : countryByCode.name;
    }
    
    // Search by name in ALL_COUNTRIES
    const countryByName = ALL_COUNTRIES.find(c => 
      c.name.toLowerCase() === countryValue.toLowerCase() ||
      c.nativeName?.toLowerCase() === countryValue.toLowerCase()
    );
    if (countryByName) {
      const key = `location.countries.${countryByName.code}`;
      const translated = t(key);
      return translated !== key ? translated : countryByName.name;
    }
    
    // Return as-is if not found
    return countryValue;
  };
  
  const countryName = getLocalizedCountryName(request.country);
  
  return (
    <Card 
      className="hover:shadow-lg transition-all cursor-pointer border-l-4 border-l-orange-500"
      onClick={onClick}
    >
      <CardContent className="p-5">
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <h3 className="font-semibold text-lg text-gray-900 line-clamp-1">
              {translatedTitle}
              {isContentTranslated && (
                <Languages className="inline-block ml-2 h-3.5 w-3.5 text-blue-500" title={t('common.translated')} />
              )}
            </h3>
            <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
              <MapPin className="h-3.5 w-3.5" />
              {request.city ? `${request.city}, ` : ''}{countryName}
            </p>
          </div>
          <Badge className={urgencyConfig.color}>{t(urgencyConfig.labelKey)}</Badge>
        </div>
        
        <p className="text-sm text-gray-600 line-clamp-2 mb-3">{translatedDesc}</p>
        
        <div className="flex flex-wrap gap-2 mb-3">
          <Badge variant="outline">{(() => {
            const key = `categories.${request.category}`;
            const translated = t(key);
            return translated !== key ? translated : request.category.replace(/_/g, ' ');
          })()}</Badge>
          {durationConfig && (
            <Badge variant="secondary" className="bg-purple-50 text-purple-700">
              <Clock className="h-3 w-3 mr-1" />
              {t(durationConfig.labelKey)}
            </Badge>
          )}
          {request.visibility === 'cross_border' && (
            <Badge variant="secondary" className="bg-blue-50 text-blue-700">
              <Globe className="h-3 w-3 mr-1" />
              {t('serviceRequest.crossBorder')}
            </Badge>
          )}
          {request.transport_details && (
            <Badge variant="secondary" className="bg-orange-50 text-orange-700">
              <Truck className="h-3 w-3 mr-1" />
              {t('serviceRequest.transport')}
            </Badge>
          )}
        </div>
        
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>{request.responses_count} {t('serviceRequest.responses')}</span>
          <span>{new Date(request.created_at).toLocaleDateString()}</span>
        </div>
      </CardContent>
    </Card>
  );
}

// Create Request Form Component
function CreateRequestForm({ onSuccess }) {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState(null);
  const [previewMatches, setPreviewMatches] = useState(null);
  
  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [durationNeeded, setDurationNeeded] = useState('1_week');  // NEW
  const [urgency, setUrgency] = useState('normal');
  const [visibility, setVisibility] = useState('country');
  const [allowedCountries, setAllowedCountries] = useState([]);
  const [radiusKm, setRadiusKm] = useState(50);
  const [pricingType, setPricingType] = useState('request_quote');
  const [budgetMin, setBudgetMin] = useState('');
  const [budgetMax, setBudgetMax] = useState('');
  const [providerType, setProviderType] = useState('both');
  const [requireVerified, setRequireVerified] = useState(false);
  
  // Transport fields
  const [isTransport, setIsTransport] = useState(false);
  const [fromCity, setFromCity] = useState('');
  const [fromCountry, setFromCountry] = useState('');
  const [toCity, setToCity] = useState('');
  const [toCountry, setToCountry] = useState('');
  const [itemDescription, setItemDescription] = useState('');
  const [weightKg, setWeightKg] = useState('');
  
  // Guest fields
  const [guestEmail, setGuestEmail] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  
  // Fetch config on mount
  useEffect(() => {
    fetchConfig();
  }, []);
  
  const fetchConfig = async () => {
    try {
      const response = await fetch(`${API}/api/service-requests/config`);
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Failed to fetch config:', error);
    }
  };
  
  const previewProviders = async () => {
    try {
      const payload = buildPayload();
      const response = await fetch(`${API}/api/service-requests/preview-matches`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const data = await response.json();
        setPreviewMatches(data);
      }
    } catch (error) {
      console.error('Preview failed:', error);
    }
  };
  
  const buildPayload = () => {
    const payload = {
      title,
      description,
      category,
      country,
      city: city || null,
      radius_km: radiusKm,
      urgency,
      duration_needed: durationNeeded,  // MANDATORY
      visibility,
      allowed_countries: visibility === 'cross_border' ? allowedCountries : [],
      pricing_type: pricingType,
      budget_min: budgetMin ? parseFloat(budgetMin) : null,
      budget_max: budgetMax ? parseFloat(budgetMax) : null,
      budget,  // universal budget block (BudgetSpec shape)
      provider_type: providerType,
      require_verified: requireVerified,
      required_skills: [],
      required_certifications: [],
      language_requirements: [],
      excluded_provider_ids: [],
      excluded_company_ids: []
    };
    
    // Add transport details if applicable
    if (isTransport && fromCity && toCity) {
      payload.transport_details = {
        from_location: { country: fromCountry || country, city: fromCity },
        to_location: { country: toCountry || country, city: toCity },
        item_description: itemDescription || null,
        weight_kg: weightKg ? parseFloat(weightKg) : null
      };
    }
    
    // Add guest info if not logged in
    if (!user) {
      payload.client_email = guestEmail;
      payload.client_name = guestName || null;
      payload.client_phone = guestPhone || null;
    }
    
    return payload;
  };
  
  const handleSubmit = async () => {
    // Validation
    if (!title || title.length < 5) {
      toast.error('Please enter a title (min 5 characters)');
      return;
    }
    if (!description || description.length < 20) {
      toast.error('Please enter a description (min 20 characters)');
      return;
    }
    if (!category) {
      toast.error('Please select a category');
      return;
    }
    if (!country) {
      toast.error('Please select a country');
      return;
    }
    if (!user && !guestEmail) {
      toast.error('Please enter your email to receive responses');
      return;
    }
    
    setLoading(true);
    try {
      const payload = buildPayload();
      
      const response = await fetch(`${API}/api/service-requests/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Service request posted! Matching providers will be notified.');
        if (onSuccess) onSuccess(data);
        navigate(`/service-requests/${data.id}`);
      } else {
        const error = await response.json();
        toast.error(error.detail);
      }
    } catch (error) {
      toast.error('Failed to create request');
    } finally {
      setLoading(false);
    }
  };
  
  // Group categories
  const groupedCategories = config?.categories ? 
    Object.entries(config.categories).reduce((acc, [key, val]) => {
      const group = val.group;
      if (!acc[group]) acc[group] = [];
      acc[group].push({ key, ...val });
      return acc;
    }, {}) : {};
  
  return (
    <div className="space-y-6">
      {/* Step 1: Basic Info */}
      {step === 1 && (
        <div className="space-y-4">
          <div>
            <Label htmlFor="title">{t('globalService.form.whatDoYouNeed')}</Label>
            <Input
              id="title"
              placeholder={t('globalService.form.titlePlaceholder')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1.5"
            />
          </div>
          
          <div>
            <Label htmlFor="description">{t('globalService.form.describeRequest')}</Label>
            <Textarea
              id="description"
              placeholder={t('globalService.form.descriptionPlaceholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-1.5 min-h-[120px]"
            />
          </div>
          
          <div>
            <Label>{t('common.category')}</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
              {Object.entries(CATEGORY_GROUPS).map(([groupKey, group]) => (
                <Button
                  key={groupKey}
                  type="button"
                  variant={category && groupedCategories[groupKey]?.some(c => c.key === category) ? "default" : "outline"}
                  className="justify-start h-auto py-3"
                  onClick={() => setCategory(groupedCategories[groupKey]?.[0]?.key)}
                >
                  <group.icon className="h-4 w-4 mr-2" />
                  {t(group.nameKey)}
                </Button>
              ))}
            </div>
            
            {category && (
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(groupedCategories).map(([group, cats]) => (
                    <React.Fragment key={group}>
                      <div className="px-2 py-1 text-xs font-semibold text-gray-500">
                        {t(CATEGORY_GROUPS[group]?.nameKey) || group}
                      </div>
                      {cats.map(cat => (
                        <SelectItem key={cat.key} value={cat.key}>
                          {(() => {
                            const key = `categories.${cat.key}`;
                            const translated = t(key);
                            return translated !== key ? translated : cat.name;
                          })()}
                        </SelectItem>
                      ))}
                    </React.Fragment>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          
          <Button 
            onClick={() => setStep(2)} 
            className="w-full"
            disabled={!title || !description || !category}
          >
            {t('common.continue')} <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}
      
      {/* Step 2: Location & Visibility */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t('common.country')}</Label>
              <div className="mt-1.5">
                <LocationAutocomplete
                  type="country"
                  value={country}
                  onChange={setCountry}
                  placeholder={t('location.searchCountry')}
                  allowFreeText={true}
                />
              </div>
            </div>
            <div>
              <Label>{t('globalService.form.cityOptional')}</Label>
              <div className="mt-1.5">
                <LocationAutocomplete
                  type="city"
                  value={city}
                  onChange={setCity}
                  countryCode={country}
                  placeholder={t('location.searchCity')}
                  allowFreeText={true}
                />
              </div>
            </div>
          </div>
          
          <div>
            <Label>{t('globalService.form.serviceRadius', { km: radiusKm })}</Label>
            <Slider
              value={[radiusKm]}
              onValueChange={(v) => setRadiusKm(v[0])}
              min={10}
              max={500}
              step={10}
              className="mt-2"
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('globalService.form.radiusDescription')}
            </p>
          </div>
          
          <div>
            <Label>{t('globalService.form.visibilityScope')}</Label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {[
                { value: 'local', labelKey: 'globalService.form.localOnly', icon: MapPin },
                { value: 'country', labelKey: 'globalService.form.countryWide', icon: Building2 },
                { value: 'cross_border', labelKey: 'globalService.form.crossBorder', icon: Globe }
              ].map(opt => (
                <Button
                  key={opt.value}
                  type="button"
                  variant={visibility === opt.value ? "default" : "outline"}
                  className="h-auto py-3 flex-col"
                  onClick={() => setVisibility(opt.value)}
                >
                  <opt.icon className="h-4 w-4 mb-1" />
                  <span className="text-xs">{t(opt.labelKey)}</span>
                </Button>
              ))}
            </div>
          </div>
          
          {visibility === 'cross_border' && (
            <div>
              <Label>{t('globalService.form.allowProviders')}</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {ALL_COUNTRIES.filter(c => c.code !== country).slice(0, 20).map(c => (
                  <Badge
                    key={c.code}
                    variant={allowedCountries.includes(c.code) ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => setAllowedCountries(prev =>
                      prev.includes(c.code) 
                        ? prev.filter(x => x !== c.code)
                        : [...prev, c.code]
                    )}
                  >
                    {c.flag} {(() => {
                      const key = `location.countries.${c.code}`;
                      const translated = t(key);
                      return translated !== key ? translated : c.name;
                    })()}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
              {t('common.back')}
            </Button>
            <Button onClick={() => setStep(3)} className="flex-1" disabled={!country}>
              {t('common.continue')} <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
      
      {/* Step 3: Details & Timing */}
      {step === 3 && (
        <div className="space-y-4">
          {/* Duration - MANDATORY */}
          <div>
            <Label className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {t('globalService.form.howLongNeeded')} *
            </Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
              {DURATION_OPTIONS.map(opt => (
                <Button
                  key={opt.value}
                  type="button"
                  variant={durationNeeded === opt.value ? "default" : "outline"}
                  className="h-auto py-3 justify-start"
                  onClick={() => setDurationNeeded(opt.value)}
                >
                  <div className="text-left">
                    <div className="font-medium">{t(opt.labelKey)}</div>
                    <div className="text-xs opacity-70">{t(opt.descriptionKey)}</div>
                  </div>
                </Button>
              ))}
            </div>
          </div>
          
          <Separator />
          
          <div>
            <Label>{t('globalService.form.urgency')}</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {URGENCY_OPTIONS.map(opt => (
                <Button
                  key={opt.value}
                  type="button"
                  variant={urgency === opt.value ? "default" : "outline"}
                  className="h-auto py-3 justify-start"
                  onClick={() => setUrgency(opt.value)}
                >
                  <div className="text-left">
                    <div className="font-medium">{t(opt.labelKey)}</div>
                    <div className="text-xs opacity-70">{t(opt.descriptionKey)}</div>
                  </div>
                </Button>
              ))}
            </div>
          </div>
          
          <div>
            <Label>{t('globalService.form.pricing')}</Label>
            <Select value={pricingType} onValueChange={setPricingType}>
              <SelectTrigger className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="request_quote">{t('globalService.form.requestQuote')}</SelectItem>
                <SelectItem value="hourly">{t('globalService.form.hourlyRate')}</SelectItem>
                <SelectItem value="fixed">{t('globalService.form.fixedPrice')}</SelectItem>
                <SelectItem value="negotiable">{t('globalService.form.negotiable')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {(pricingType === 'fixed' || pricingType === 'hourly') && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('globalService.form.budgetMin')}</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={budgetMin}
                  onChange={(e) => setBudgetMin(e.target.value)}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>{t('globalService.form.budgetMax')}</Label>
                <Input
                  type="number"
                  placeholder="1000"
                  value={budgetMax}
                  onChange={(e) => setBudgetMax(e.target.value)}
                  className="mt-1.5"
                />
              </div>
            </div>
          )}

          {/* Universal Budget block — works alongside legacy pricing fields. */}
          <div className="p-3 border rounded-lg bg-white">
            <BudgetSection
              category={category}
              country={country}
              value={budget}
              onChange={setBudget}
            />
          </div>
          
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <Label>{t('globalService.form.isTransportRequest')}</Label>
              <p className="text-xs text-gray-500">{t('globalService.form.addPickupDelivery')}</p>
            </div>
            <Switch checked={isTransport} onCheckedChange={setIsTransport} />
          </div>
          
          {isTransport && (
            <Card className="p-4 bg-blue-50 border-blue-200">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Truck className="h-4 w-4" /> {t('globalService.form.transportDetails')}
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t('globalService.form.fromCity')}</Label>
                  <LocationAutocomplete
                    type="city"
                    value={fromCity}
                    onChange={setFromCity}
                    placeholder={t('globalService.form.pickupCity')}
                    allowFreeText={true}
                  />
                </div>
                <div>
                  <Label className="text-xs">{t('globalService.form.toCity')}</Label>
                  <LocationAutocomplete
                    type="city"
                    value={toCity}
                    onChange={setToCity}
                    placeholder={t('globalService.form.deliveryCity')}
                    allowFreeText={true}
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">{t('globalService.form.itemDescription')}</Label>
                  <Input
                    placeholder={t('globalService.form.itemPlaceholder')}
                    value={itemDescription}
                    onChange={(e) => setItemDescription(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">{t('globalService.form.weightKg')}</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={weightKg}
                    onChange={(e) => setWeightKg(e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            </Card>
          )}
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep(2)} className="flex-1">
              {t('common.back')}
            </Button>
            <Button onClick={() => { previewProviders(); setStep(4); }} className="flex-1">
              {t('common.continue')} <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
      
      {/* Step 4: Contact & Submit */}
      {step === 4 && (
        <div className="space-y-4">
          {/* Provider preferences */}
          <div>
            <Label>{t('globalService.form.providerType')}</Label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {[
                { value: 'both', labelKey: 'globalService.form.any' },
                { value: 'company', labelKey: 'globalService.form.companies' },
                { value: 'individual', labelKey: 'globalService.form.individuals' }
              ].map(opt => (
                <Button
                  key={opt.value}
                  type="button"
                  variant={providerType === opt.value ? "default" : "outline"}
                  onClick={() => setProviderType(opt.value)}
                >
                  {t(opt.labelKey)}
                </Button>
              ))}
            </div>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <Label>{t('globalService.form.requireVerified')}</Label>
              <p className="text-xs text-gray-500">{t('globalService.form.verifiedDescription')}</p>
            </div>
            <Switch checked={requireVerified} onCheckedChange={setRequireVerified} />
          </div>
          
          {/* Guest contact info */}
          {!user && (
            <Card className="p-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Mail className="h-4 w-4" /> {t('globalService.form.yourContactInfo')}
              </h4>
              <p className="text-sm text-gray-500 mb-3">
                {t('globalService.form.receiveResponses')}
              </p>
              <div className="space-y-3">
                <div>
                  <Label>{t('common.email')} *</Label>
                  <Input
                    type="email"
                    placeholder={t('globalService.form.emailPlaceholder')}
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{t('globalService.form.nameOptional')}</Label>
                    <Input
                      placeholder={t('globalService.form.yourName')}
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>{t('globalService.form.phoneOptional')}</Label>
                    <Input
                      type="tel"
                      placeholder="+358..."
                      value={guestPhone}
                      onChange={(e) => setGuestPhone(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </Card>
          )}
          
          {/* Preview matches */}
          {previewMatches && (
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-700">
                <strong>{previewMatches.total_potential_matches} {t('globalService.form.providers')}</strong> {t('globalService.form.matchYourRequest')}
              </AlertDescription>
            </Alert>
          )}
          
          {/* Summary */}
          <Card className="p-4 bg-gray-50">
            <h4 className="font-medium mb-2">{t('globalService.form.requestSummary')}</h4>
            <div className="text-sm space-y-1 text-gray-600">
              <p><strong>{t('globalService.form.titleLabel')}:</strong> {title}</p>
              <p><strong>{t('common.category')}:</strong> {(() => {
                const key = `categories.${category}`;
                const translated = t(key);
                return translated !== key ? translated : category;
              })()}</p>
              <p><strong>{t('common.location')}:</strong> {city ? `${city}, ` : ''}{(() => {
                const key = `location.countries.${country}`;
                const translated = t(key);
                return translated !== key ? translated : country;
              })()}</p>
              <p><strong>{t('globalService.form.durationLabel')}:</strong> {t(DURATION_OPTIONS.find(d => d.value === durationNeeded)?.labelKey || durationNeeded)}</p>
              <p><strong>{t('globalService.form.urgency')}:</strong> {t(URGENCY_OPTIONS.find(u => u.value === urgency)?.labelKey || urgency)}</p>
              <p><strong>{t('globalService.form.visibilityScope')}:</strong> {t(`globalService.form.${visibility.replace('_', '')}`) || visibility.replace('_', '-')}</p>
              {isTransport && fromCity && toCity && (
                <p><strong>{t('globalService.form.route')}:</strong> {fromCity} → {toCity}</p>
              )}
            </div>
          </Card>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep(3)} className="flex-1">
              {t('common.back')}
            </Button>
            <Button 
              onClick={handleSubmit} 
              className="flex-1"
              disabled={loading || (!user && !guestEmail)}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              {t('globalService.postRequest')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Main Page Component
export function GlobalServiceRequestPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [activeTab, setActiveTab] = useState('browse');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    country: searchParams.get('country'),
    category: searchParams.get('category'),
    search: ''
  });
  
  useEffect(() => {
    fetchRequests();
  }, [filters]);
  
  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.country) params.append('country', filters.country);
      if (filters.category) params.append('category', filters.category);
      if (filters.search) params.append('search', filters.search);
      
      const response = await fetch(`${API}/api/service-requests/?${params}`);
      if (response.ok) {
        const data = await response.json();
        setRequests(data);
      }
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-orange-600 to-orange-500 text-white">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <h1 className="text-3xl md:text-4xl font-bold mb-3">
            {t('globalService.title')}
          </h1>
          <p className="text-orange-100 text-lg max-w-2xl">
            {t('globalService.subtitle')}
          </p>
          
          <div className="flex gap-3 mt-6">
            <Button 
              size="lg" 
              className="bg-white text-orange-600 hover:bg-orange-50"
              onClick={() => setActiveTab('create')}
            >
              <Plus className="h-5 w-5 mr-2" />
              {t('globalService.postRequest')}
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-orange-700"
              onClick={() => navigate('/settings/provider-preferences')}
            >
              <Building2 className="h-5 w-5 mr-2" />
              {t('globalService.registerAsProvider')}
            </Button>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="browse">{t('globalService.browseRequests')}</TabsTrigger>
            <TabsTrigger value="create">{t('globalService.createRequest')}</TabsTrigger>
          </TabsList>
          
          <TabsContent value="browse">
            {/* Filters */}
            <Card className="mb-6">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-3">
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder={t('globalService.searchPlaceholder')}
                        value={filters.search}
                        onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  
                  <div className="w-48">
                    <LocationAutocomplete
                      type="country"
                      value={filters.country}
                      onChange={(v) => setFilters(f => ({ ...f, country: v }))}
                      placeholder={t('serviceRequest.filterCountry')}
                      allowFreeText={true}
                      data-testid="country-filter"
                    />
                  </div>
                  
                  <Select 
                    value={filters.category} 
                    onValueChange={(v) => setFilters(f => ({ ...f, category: v === '__all__' ? '' : v }))}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder={t('serviceRequest.allCategories')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">{t('serviceRequest.allCategories')}</SelectItem>
                      {Object.entries(CATEGORY_GROUPS).map(([key, group]) => (
                        <SelectItem key={key} value={key}>
                          {t(group.nameKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
            
            {/* Results */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : requests.length === 0 ? (
              <Card className="p-8 text-center">
                <Package className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <h3 className="font-semibold text-lg mb-2">{t('serviceRequest.noRequestsFound')}</h3>
                <p className="text-gray-500 mb-4">{t('serviceRequest.beFirstToPost')}</p>
                <Button onClick={() => setActiveTab('create')}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t('serviceRequest.postRequest')}
                </Button>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {requests.map(request => (
                  <ServiceRequestCard 
                    key={request.id} 
                    request={request}
                    onClick={() => navigate(`/service-requests/${request.id}`)}
                  />
                ))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="create">
            <Card>
              <CardHeader>
                <CardTitle>{t('globalService.createRequest')}</CardTitle>
                <CardDescription>
                  {t('globalService.createDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <CreateRequestForm />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default GlobalServiceRequestPage;
